import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ru from './locales/ru.json';
import en from './locales/en.json';

/**
 * Internationalisation (react-i18next).
 *
 * Russian is the project's first language (the cispracs.ru community); English
 * is the alternative. Resources are bundled (not lazy-loaded) so the very first
 * paint is already translated — no Suspense flash, no FOUC. Strings live in
 * `locales/<lang>.json` as nested keys grouped by surface (landing, settings,
 * …). Components read them with `useTranslation()` / `t('landing.title')`, or
 * `<Trans>` when a string embeds markup.
 *
 * Language resolution (detector): the user's saved choice (localStorage key
 * `bunker.lang`) wins; otherwise the browser language if it's ru/en; otherwise
 * ru. Changing it in Settings calls `i18n.changeLanguage(id)`, which persists
 * back to localStorage via the detector cache.
 */
export const SUPPORTED_LANGUAGES = [
  { id: 'ru', label: 'Русский' },
  { id: 'en', label: 'English' },
] as const;

export type LangId = (typeof SUPPORTED_LANGUAGES)[number]['id'];

export const LANG_STORAGE_KEY = 'bunker.lang';

/** The supported UI language for an i18next code ('en-US' → 'en'; unknown → 'ru'). */
export function uiLanguage(code: string | undefined): LangId {
  const base = (code ?? '').toLowerCase().split('-')[0];
  return SUPPORTED_LANGUAGES.some(({ id }) => id === base) ? (base as LangId) : 'ru';
}

// <html lang> follows the UI language: screen readers pick their voice from
// it, and the browser offers translation by it. index.html ships `ru`, so an
// English UI was announced as Russian. Subscribed before init so the first
// detected language is applied too.
i18n.on('languageChanged', (code) => {
  if (typeof document !== 'undefined') document.documentElement.lang = uiLanguage(code);
});

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ru: { translation: ru },
      en: { translation: en },
    },
    fallbackLng: 'ru',
    supportedLngs: ['ru', 'en'],
    // Map regional tags (e.g. 'en-US', 'ru-RU') onto the base language.
    nonExplicitSupportedLngs: true,
    interpolation: {
      // React already escapes interpolated values — double-escaping would
      // mangle them.
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ['localStorage'],
    },
    react: {
      // Resources are bundled synchronously, so we don't need Suspense.
      useSuspense: false,
    },
  });

export default i18n;
